import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Modal from "react-modal";
import { toast } from "react-toastify";
import { handleCheckDiscount, handleCreateOrder, handleAutoApplyDiscount } from "../../services/order";
import {
  onOrderSubmit,
  removeFromCart,
  updateCartItem,
} from "../../store/slice/cart";
import isValidURL from "../../ValidateUrl";
import { shefDetailPath } from "../../shefPath";
import { updateUser } from "../../store/slice/user";
import { handleGetDefaultSetting } from "../../services/default_setting";
import convertTo12Hour from "../../convertTo12Hours";
import {
  handleCalculateDeliveryCharge,
  handleGetChefAddress,
} from "../../services/shef";
import { handleGetChefOfflineWindows } from "../../services/get_without_auth";
import MapWithConditionalRendering from "../components/Map/MapWithConditionRendering";

// Kitchen Offline — gates order PLACEMENT only. Advance orders for a
// delivery day that later falls inside a blackout are allowed; the chef
// simply cannot receive new orders while inside an offline window.
// Mirrors ChefOfflineWindow::isOfflineNow on the backend.
const parseHM = (t) => {
  const [h, m] = String(t || "")
    .trim()
    .split(":")
    .map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
};

// Kitchen Offline — is the chef inside an offline window right now?
const kitchenOfflineNow = (windows) => {
  if (!Array.isArray(windows) || !windows.length) return false;
  const now = new Date();
  const dow = now.getDay();
  const mins = now.getHours() * 60 + now.getMinutes();
  return windows.some((w) => {
    if (w.all_day) return dow === w.day_of_week;
    const start = parseHM(w.start_time);
    const end = parseHM(w.end_time);
    if (end > start) {
      return dow === w.day_of_week && mins >= start && mins <= end;
    }
    // Overnight window: active on start day after start, next day before end
    return (
      (dow === w.day_of_week && mins >= start) ||
      (dow === (w.day_of_week + 1) % 7 && mins <= end)
    );
  });
};

// Kitchen Offline — minutes until the next offline window starts.
// Returns the soonest start within `thresholdMins`, or null if none is near.
const kitchenOfflineStartsIn = (windows, thresholdMins = 15) => {
  if (!Array.isArray(windows) || !windows.length) return null;
  const now = new Date();
  const nowMins = now.getHours() * 60 + now.getMinutes();
  let soonest = null;
  windows.forEach((w) => {
    const daysAhead = (w.day_of_week - now.getDay() + 7) % 7;
    const startMins = w.all_day ? 0 : parseHM(w.start_time);
    const diff = daysAhead * 1440 + startMins - nowMins;
    if (diff > 0 && diff <= thresholdMins && (soonest === null || diff < soonest)) {
      soonest = diff;
    }
  });
  return soonest;
};

export const Checkout = () => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { userInfo, authToken } = useSelector((state) => state.user);
  const { cartItem } = useSelector((state) => state.cart);
  const { chefId, chef_index } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeButton, setActiveButton] = useState(null);
  const [chefAddress, setChefAddress] = useState(null);
  const [offlineWindows, setOfflineWindows] = useState([]);

  const [bykeaFare, setBykeaFare] = useState(0);
  const [isBykeaLoading, setIsBykeaLoading] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [coordinates, setCoordinates] = useState({ lat: "", lng: "" });
  const [nearLandmark, setNearLandmark] = useState("");
  const [addressPlace, setAddressPlace] = useState("home");

  // ── Local geocoder ref: Checkout owns its own geocoding for rich parsing ──
  const geocoderRef = useRef(null);

  // Lazily init geocoder once Google Maps SDK is available
  const getGeocoder = () => {
    if (!geocoderRef.current && window.google?.maps?.Geocoder) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    return geocoderRef.current;
  };

  const orderInitial = {
    chef_id: 1,
    chef_availability_id: 1,
    delivery_time: "",
    delivery_slot: "",
    name: [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" "),
    email: userInfo?.email || "",
    phone: userInfo?.phone || "",
    status: 1,
    payment_mode: 1,
    delivery_notes: "",
    sub_total: 0,
    delivery_percentage: 0,
    delivery_price: 0,
    service_fee: 0,
    discount_price: 0,
    tip_price: 0,
    chef_earning_price: 0,
    total_price: 0,
  };

  const orderDetailInitial = [
    {
      name: "",
      user_menu_id: 1,
      unit_price: 0,
      quantity: 0,
      platform_percentage: 0,
      platform_price: 0,
      delivery_percentage: 0,
      delivery_price: 0,
      chef_price: 0,
    },
  ];

  const orderDeliveryAddressInitial = {
    home_house_no: "",
    home_street_address: "",
    home_city: "",
    home_addition_direction: "",
    office_department: "",
    office_floor: "",
    office_company: "",
    office_building_no: "",
    office_street_address: "",
    office_city: "",
    office_addition_direction: "",
    apartment_name: "",
    apartment_apartment_no: "",
    apartment_floor: "",
    apartment_street_address: "",
    apartment_city: "",
    apartment_addition_direction: "",
    line2: "",
    latitude: "",
    longitude: "",
    name: [userInfo?.first_name, userInfo?.last_name].filter(Boolean).join(" "),
    phone: userInfo?.phone || "",
    postal_code: "",
    city: "",
    state: "",
    delivery_instruction: "",
    delivery_notes: "",
    address_type: "home",
  };

  const [order, setOrder] = useState(orderInitial);
  const [orderDetails, setOrderDetails] = useState(orderDetailInitial);
  const [orderDeliveryAddress, setOrderDeliveryAddress] = useState(
    orderDeliveryAddressInitial,
  );
  const [isPending, setIsPending] = useState(false);

  const [promoCode, setPromoCode] = useState({
    code: "",
    order_total: 0,
    menus: [],
  });
  const [defaultSetting, setDefaultSetting] = useState("");

  const updateOrder = (field) => setOrder((prev) => ({ ...prev, ...field }));
  const updateOrderDeliveryAddress = (field) =>
    setOrderDeliveryAddress((prev) => ({ ...prev, ...field }));

  const handleButtonClick = (buttonId, percent) => {
    setActiveButton(buttonId);
    calculateTip(percent);
  };

  const calculateDeliveryCharge = async () => {
    if (!chefAddress || !coordinates.lat || !coordinates.lng) return;

    const chefLat = parseFloat(chefAddress?.addresses[0]?.latitude || 0);
    const chefLon = parseFloat(chefAddress?.addresses[0]?.longitude || 0);
    const customerLat = coordinates.lat;
    const customerLon = coordinates.lng;

    if (!chefLat || !chefLon || !customerLat || !customerLon) return;

    setIsBykeaLoading(true);

    try {
      const response = await handleCalculateDeliveryCharge(authToken, {
        pickup: { lat: chefLat, lng: chefLon },
        dropoff: { lat: customerLat, lng: customerLon },
        customer_phone: order.phone,
      });

      const fareData = response?.data || {};

      setBykeaFare(fareData.fare || 0);
    } catch (err) {
      console.error("Delivery charge error:", err);
      setBykeaFare(0);
    } finally {
      setIsBykeaLoading(false);
    }
  };

  const fetchChefAddress = async () => {
    try {
      const targetChefId = chefId || cartItem[0]?.id;
      if (targetChefId) {
        const addr = await handleGetChefAddress(authToken, targetChefId);
        setChefAddress(addr);
      }
    } catch (err) {
      console.error("Error fetching chef address", err);
    }
  };

  // ── Rich address parser: receives a full geocoder results array ───────────
  const parseAndApplyGeocoderResults = useCallback((results) => {
    if (!Array.isArray(results) || results.length === 0) return;

    const typePriority = [
      "street_address",
      "premise",
      "subpremise",
      "route",
      "neighborhood",
      "sublocality_level_2",
      "sublocality_level_1",
      "sublocality",
    ];
    let best = results[0];
    for (const type of typePriority) {
      const found = results.find((r) => r.types.includes(type));
      if (found) {
        best = found;
        break;
      }
    }

    const components = best.address_components;
    const get = (types) =>
      components.find((c) => c.types.some((t) => types.includes(t)))
        ?.long_name || "";

    let houseNumber =
      get(["street_number"]) ||
      get(["premise"]) ||
      get(["subpremise"]) ||
      get(["plus_code"]);
    if (!houseNumber) {
      for (const result of results) {
        const c = result.address_components;
        const found =
          c.find((x) => x.types.includes("street_number"))?.long_name ||
          c.find((x) => x.types.includes("premise"))?.long_name ||
          c.find((x) => x.types.includes("subpremise"))?.long_name;
        if (found) {
          houseNumber = found;
          break;
        }
      }
    }
    if (!houseNumber && best.formatted_address) {
      const match = best.formatted_address.match(
        /^(Plot\s*[\w\d/-]+|House\s*[\w\d/-]+|H[#\s-]?[\d\w]+|[\d][\w\d/-]*)/i,
      );
      if (match) houseNumber = match[0].trim();
    }

    const streetParts =
      components
        .filter((c) =>
          c.types.some((t) =>
            [
              "route",
              "neighborhood",
              "sublocality",
              "sublocality_level_1",
              "sublocality_level_2",
              "landmark",
            ].includes(t),
          ),
        )
        .map((c) => c.long_name)
        .join(", ") ||
      best.formatted_address?.split(",").slice(0, -2).join(",").trim() ||
      "";

    const city =
      get(["locality"]) ||
      get(["administrative_area_level_2"]) ||
      get(["administrative_area_level_1"]);

    const landmarkTypes = [
      "point_of_interest",
      "establishment",
      "mosque",
      "church",
      "hospital",
      "school",
      "park",
      "shopping_mall",
      "transit_station",
    ];
    let landmark = null;
    for (const result of results) {
      if (result.types.some((t) => landmarkTypes.includes(t)) && result.name) {
        landmark = result.name;
        break;
      }
    }
    if (!landmark) {
      landmark =
        get(["neighborhood"]) ||
        get(["sublocality_level_2"]) ||
        get(["sublocality_level_1"]) ||
        get(["sublocality"]);
    }

    setNearLandmark(landmark || "");

    updateOrderDeliveryAddress({
      home_street_address: streetParts,
      home_house_no: houseNumber,
      home_city: city,
      office_street_address: streetParts,
      office_building_no: houseNumber,
      office_city: city,
      apartment_street_address: streetParts,
      apartment_apartment_no: houseNumber,
      apartment_city: city,
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handleReverseGeocode: receives a formatted address STRING from the map,
  //    re-geocodes locally to get the full results array for rich parsing ────
  const handleReverseGeocode = useCallback(
    (formattedAddress) => {
      // formattedAddress is a string — re-geocode to get full results array
      const geocoder = getGeocoder();
      if (!geocoder) {
        console.warn("Geocoder not ready yet");
        return;
      }

      geocoder.geocode({ address: formattedAddress }, (results, status) => {
        if (status === "OK" && Array.isArray(results) && results.length > 0) {
          parseAndApplyGeocoderResults(results);
        } else {
          console.warn("Re-geocode failed:", status);
        }
      });
    },
    [parseAndApplyGeocoderResults],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePlaceSelect = ({ lat, lng }) => {
    setCoordinates({ lat, lng });
    updateOrderDeliveryAddress({ latitude: lat, longitude: lng });
  };

  const onDragEnd = (event) => {
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    setCoordinates({ lat, lng });
    updateOrderDeliveryAddress({ latitude: lat, longitude: lng });
  };

  const getGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setError(null);
          setIsLoading(false);
          setIsModalOpen(false);
          updateOrderDeliveryAddress({ latitude, longitude });
          setCoordinates({ lat: latitude, lng: longitude });
        },
        () => {
          setIsLoading(false);
          setIsModalOpen(true);
          setError("Location access is required.");
        },
      );
    } else {
      setError("Geolocation is not supported by your browser.");
      setIsModalOpen(true);
      setIsLoading(false);
    }
  };

  // Effects
  useEffect(() => {
    const initLocation = async () => {
      // Try to use the last saved profile address
      const addresses = userInfo?.user_addresses || userInfo?.chef_addresses;
      const lastSaved = addresses?.length > 0 ? addresses[addresses.length - 1] : null;

      const hasValidSaved =
        lastSaved &&
        lastSaved.address &&
        lastSaved.address !== "NA" &&
        lastSaved.latitude &&
        lastSaved.longitude;

      if (hasValidSaved) {
        const lat = parseFloat(lastSaved.latitude);
        const lng = parseFloat(lastSaved.longitude);

        // Set coordinates
        setCoordinates({ lat, lng });
        updateOrderDeliveryAddress({ latitude: lat, longitude: lng });

        // Reverse-geocode the saved address string to fill detailed fields
        const geocoder = getGeocoder();
        if (geocoder) {
          geocoder.geocode({ address: lastSaved.address }, (results, status) => {
            if (status === "OK" && results?.length) {
              parseAndApplyGeocoderResults(results);
            }
          });
        }

        setIsLoading(false);
        return; // done, no need for GPS
      }

      // Fallback to browser geolocation
      getGeolocation();
    };

    if (authToken && cartItem.length > 0) {
      fetchChefAddress();
      initLocation();
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "begin_checkout",
    });

    const fetchSettings = async () => {
      try {
        const response = await handleGetDefaultSetting(authToken);
        setDefaultSetting(response);
      } catch (err) {
        console.error(err);
      }
    };
    fetchSettings();
  }, []);

  // Kitchen Offline — fetch this chef's blackout windows
  useEffect(() => {
    if (!chefId) return;
    let cancelled = false;
    handleGetChefOfflineWindows(chefId)
      .then((data) => {
        if (!cancelled) setOfflineWindows(Array.isArray(data) ? data : []);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [chefId]);

  useEffect(() => {
    if (userInfo.last_order_address?.order_delivery_address) {
      const last = userInfo.last_order_address.order_delivery_address;
      updateOrderDeliveryAddress({
        home_house_no: last?.home_house_no || "",
        home_street_address: last?.home_street_address || "",
        home_city: last?.home_city || "",
        home_addition_direction: last?.home_addition_direction || "",
        office_department: last?.office_department || "",
        office_floor: last?.office_floor || "",
        office_company: last?.office_company || "",
        office_building_no: last?.office_building_no || "",
        office_street_address: last?.office_street_address || "",
        office_city: last?.office_city || "",
        office_addition_direction: last?.office_addition_direction || "",
        apartment_name: last?.apartment_name || "",
        apartment_apartment_no: last?.apartment_apartment_no || "",
        apartment_floor: last?.apartment_floor || "",
        apartment_street_address: last?.apartment_street_address || "",
        apartment_city: last?.apartment_city || "",
        apartment_addition_direction: last?.apartment_addition_direction || "",
      });
    }
  }, []);

  useEffect(() => {
    if (chefAddress && coordinates.lat && coordinates.lng) {
      calculateDeliveryCharge();
    }
  }, [chefAddress, coordinates.lat, coordinates.lng]);

  useEffect(() => {
    let sub_total = 0;
    let deliverPriceSum = 0;
    let platformPriceSum = 0;
    let chefEarningSum = 0;
    let deliveryDate = "";
    let deliverySlot = "";

    cartItem.forEach((chef, chefIndex) => {
      if (chef.id === parseInt(chefId) && chefIndex === parseInt(chef_index)) {
        deliveryDate = chef.delivery_date;
        deliverySlot = chef.delivery_slot;

        chef.menu.forEach((menu) => {
          const chef_earning_fee = menu.chef_earning_fee || 0;
          const quantity = menu.quantity || 0;

          const platformPercentageFee =
            (defaultSetting?.platform_charge_percentage / 100) *
            chef_earning_fee;
          const platform_price =
            platformPercentageFee > defaultSetting?.platform_charge
              ? platformPercentageFee
              : defaultSetting?.platform_charge;

          chefEarningSum += chef_earning_fee * quantity;
          sub_total += menu.unit_price * quantity;
          deliverPriceSum = bykeaFare;
          platformPriceSum += platform_price * quantity;
        });
      }
    });

    const city = JSON.parse(localStorage.getItem("region"));
    if (city) updateOrderDeliveryAddress({ city: city.name });

    const total =
      sub_total +
      order.tip_price +
      deliverPriceSum +
      platformPriceSum -
      order.discount_price;
    setPromoCode((prev) => ({ ...prev, order_total: total }));

    updateOrder({
      delivery_time: deliveryDate,
      delivery_slot: deliverySlot,
      chef_id: parseInt(chefId),
      city_id: city?.id,
      sub_total: sub_total,
      chef_earning_price: chefEarningSum,
      delivery_price: deliverPriceSum,
      delivery_percentage:
        chefEarningSum > 0
          ? ((deliverPriceSum / chefEarningSum) * 100).toFixed(2)
          : 0,
      service_fee: platformPriceSum,
      total_price: total,
    });

    const menuDetails = [];
    const promoCodeMenu = [];

    cartItem.forEach((chef, chefIndex) => {
      if (chef.id === parseInt(chefId) && chefIndex === parseInt(chef_index)) {
        chef.menu.forEach((menu) => {
          const platformPercentageFee =
            (defaultSetting?.platform_charge_percentage / 100) *
            menu.chef_earning_fee;
          const platform_price =
            platformPercentageFee > defaultSetting?.platform_charge
              ? platformPercentageFee
              : defaultSetting?.platform_charge;

          menuDetails.push({
            name: menu.name,
            user_menu_id: menu.id,
            unit_price: menu.unit_price,
            quantity: menu.quantity,
            platform_percentage:
              ((platform_price / menu.chef_earning_fee) * 100).toFixed(2) || 0,
            platform_price,
            delivery_percentage:
              ((bykeaFare / menu.chef_earning_fee) * 100).toFixed(2) || 0,
            delivery_price: bykeaFare,
            chef_price: menu.chef_earning_fee,
          });
          promoCodeMenu.push({ id: menu.id, qty: menu.quantity });
        });
      }
    });

    setOrderDetails(menuDetails);
    setPromoCode((prev) => ({ ...prev, menus: promoCodeMenu }));
  }, [cartItem, order.tip_price, defaultSetting, bykeaFare]);

  // Auto-apply promo — apply silently if the customer is still eligible
  const autoApplyChecked = useRef(false);
  useEffect(() => {
    if (
      autoApplyChecked.current ||
      !authToken ||
      !promoCode.menus.length ||
      !promoCode.order_total ||
      promoCode.code ||
      sessionStorage.getItem("active_discount")
    ) {
      return;
    }
    autoApplyChecked.current = true;
    (async () => {
      try {
        const res = await handleAutoApplyDiscount(authToken);
        if (!res?.eligible || !res?.discount?.code) return;
        const discountResponse = await handleCheckDiscount(authToken, {
          code: res.discount.code,
          order_total: promoCode.order_total,
          menus: promoCode.menus,
        });
        if (!discountResponse?.original?.success) return;
        const discountValue = parseFloat(
          discountResponse.original.discounted_amount,
        );
        const currentTotal = parseFloat(order.total_price);
        if (!discountValue || discountValue >= currentTotal) return;
        setPromoCode((prev) => ({ ...prev, code: res.discount.code }));
        updateOrder({
          discount_price: discountValue,
          total_price: currentTotal - discountValue,
        });
        sessionStorage.setItem(
          "active_discount",
          JSON.stringify({ amount: discountValue, code: res.discount.code }),
        );
        toast.success(`Promo ${res.discount.code} applied!`);
      } catch (e) {
        // auto-apply promo is best-effort — never block checkout
      }
    })();
  }, [authToken, promoCode.menus.length, promoCode.order_total]);

  const handlePromoCodeSubmit = async (e) => {
    if (e) e.preventDefault();
    try {
      const discountResponse = await handleCheckDiscount(authToken, promoCode);
      if (discountResponse?.original?.error) {
        toast.error(
          discountResponse?.original?.error ||
          "Something's wrong with Promo Code",
        );
        return;
      }
      const discountValue = parseFloat(
        discountResponse.original.discounted_amount,
      );
      const currentTotal = parseFloat(order.total_price);
      if (discountValue >= currentTotal) {
        toast.error("Discount amount exceeds the order total. Cannot apply.");
        return;
      }
      if (discountResponse?.original?.success) {
        toast.success("Discount applied");
        updateOrder({
          discount_price: discountValue,
          total_price: currentTotal - discountValue,
        });
        sessionStorage.setItem(
          "active_discount",
          JSON.stringify({ amount: discountValue, code: promoCode }),
        );
      }
    } catch (err) {
      console.error("Error with promo code", err);
      toast.error("Failed to apply promo code");
    }
  };

  const calculateTip = (percent) => {
    updateOrder({
      tip_price: parseFloat(
        (order.chef_earning_price * (percent / 100)).toFixed(2),
      ),
    });
  };

  const updateQuantityInStore = (chefIndex, menuIndex, quantity, operation) => {
    const updatedQuantity =
      operation === "increment"
        ? quantity + 1
        : quantity - 1 > 0
          ? quantity - 1
          : 1;
    dispatch(
      updateCartItem({
        chefIndex,
        menuIndex,
        key: "quantity",
        value: updatedQuantity,
      }),
    );
  };

  const formatDateMDY = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  };

  const onSubmit = async (e) => {
    try {
      e.preventDefault();
      setIsPending(true);
      const payload = { ...order };
      payload.delivery_time = formatDateMDY(order.delivery_time);
      payload.orderDeliveryAddress = orderDeliveryAddress;
      payload.orderDetails = orderDetails;

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "initiate_checkout",
        value: order.total_price,
        currency: "PKR",
        num_items: orderDetails.reduce((sum, d) => sum + (d.quantity || 0), 0),
      });

      const response = await handleCreateOrder(authToken, payload);
      toast.success(response.success || "Order created Successfully", {
        theme: "colored",
      });

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "purchase",
        value: order.total_price,
        currency: "PKR",
        num_items: orderDetails.reduce((sum, d) => sum + (d.quantity || 0), 0),
        order_id: response?.data?.id ?? response?.data?.order_id,
      });

      dispatch(
        onOrderSubmit({
          chefId: parseInt(chefId),
          delivery_date: order.delivery_time,
          delivery_slot: order.delivery_slot,
        }),
      );

      const updatedUserInfo = {
        ...userInfo,
        last_order_address: { order_delivery_address: orderDeliveryAddress },
      };
      localStorage.setItem("user", JSON.stringify(updatedUserInfo));
      dispatch(updateUser(updatedUserInfo));

      setOrder(orderInitial);
      setOrderDetails(orderDetailInitial);
      setOrderDeliveryAddress(orderDeliveryAddressInitial);
      navigate("/cart", { replace: true });
    } catch (err) {
      console.error("Error placing order", err);
      toast.error(err.message, { theme: "colored" });
    } finally {
      setIsPending(false);
    }
  };

  const changeAddressPlace = (placeName) => {
    setAddressPlace(placeName);
    updateOrderDeliveryAddress({ address_type: placeName });
  };

  const onRequestClose = () => setIsOpen(false);

  const onSelectExistingAddress = (address) => {
  // Set the plain address fields
  setOrderDeliveryAddress((prev) => ({
    ...prev,
    address: address?.address || "",
    line2: address?.line2 || "",
    city: address?.city || "",
    postal_code: address?.postal_code || "",
    state: address?.state || "",
  }));

  // If the address has coordinates, set them and parse details
  if (address?.latitude && address?.longitude) {
    const lat = parseFloat(address.latitude);
    const lng = parseFloat(address.longitude);
    setCoordinates({ lat, lng });
    updateOrderDeliveryAddress({ latitude: lat, longitude: lng });

    const geocoder = getGeocoder();
    if (geocoder && address.address) {
      geocoder.geocode({ address: address.address }, (results, status) => {
        if (status === "OK" && results?.length) {
          parseAndApplyGeocoderResults(results);
        }
      });
    }
  }

  setIsOpen(false);
};

  useEffect(() => {
    const storedDiscount = sessionStorage.getItem("active_discount");
    if (storedDiscount && order.total_price > 0) {
      const { amount } = JSON.parse(storedDiscount);
      const discountValue = parseFloat(amount);
      if (discountValue < order.total_price) {
        updateOrder({
          discount_price: discountValue,
          total_price: order.total_price - discountValue,
        });
      } else {
        sessionStorage.removeItem("active_discount");
      }
    }
  }, []);

  if (isLoading) return <p>Fetching data ...</p>;

  const kitchenOffline = kitchenOfflineNow(offlineWindows);
  const offlineStartsIn = kitchenOfflineStartsIn(offlineWindows);

  return (
    <>
      <div className="container mx-auto my-8 realtive">
        <div className="mb-3">
          <Link
            className="!text-black font-semibold flex items-center gap-2"
            to="/cart"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="rgba(0,0,0,1)"
            >
              <path d="M7.82843 10.9999H20V12.9999H7.82843L13.1924 18.3638L11.7782 19.778L4 11.9999L11.7782 4.22168L13.1924 5.63589L7.82843 10.9999Z"></path>
            </svg>
            <span>Back to Menu</span>
          </Link>
        </div>

        <div className="lg:text-start text-center">
          <h1 className="font-semibold text-3xl uppercase text-secondary tracking-widest">
            Checkout
          </h1>
          <div className="w-[60px] h-[2px] bg-primary my-4 lg:mx-0 mx-auto"></div>
        </div>

        <div className="grid grid-cols-12 gap-4">
          <div className="lg:col-span-7 col-span-12">
            <form onSubmit={onSubmit}>
              <div className="border border-primary border-dashed rounded-lg p-4">
                <h2 className="font-semibold text-xl uppercase text-secondary tracking-widest">
                  Delivery information
                </h2>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-2 pb-3 mb-3 bg-slate-50 px-3 pt-5 rounded">
                  <div className="relative">
                    <h4 className="text-sm font-semibold mb-1 bg-white absolute -top-2 left-2 px-1 z-20">
                      Name <span className="text-primary">*</span>
                    </h4>
                    <input
                      required
                      className="border rounded-md w-full"
                      value={order.name}
                      onChange={(e) => {
                        updateOrder({ name: e.target.value });
                        updateOrderDeliveryAddress({ name: e.target.value });
                      }}
                      placeholder="Enter Name"
                    />
                  </div>
                  <div className="relative">
                    <h4 className="text-sm font-semibold mb- bg-white absolute -top-2 left-2 px-1 z-20">
                      Phone <span className="text-primary">*</span>
                    </h4>
                    <input
                      required
                      className="border rounded-md"
                      value={order.phone}
                      onChange={(e) => {
                        updateOrder({ phone: e.target.value });
                        updateOrderDeliveryAddress({ phone: e.target.value });
                      }}
                      placeholder="Enter Phone"
                    />
                  </div>
                  <div className="relative mt-2 col-span-2">
                    <h4 className="text-sm font-semibold mb-1 bg-white absolute -top-2 left-2 px-1 z-20">
                      Email <span className="text-primary">*</span>
                    </h4>
                    <input
                      required
                      className="border rounded-md w-full"
                      value={order.email}
                      onChange={(e) => updateOrder({ email: e.target.value })}
                      placeholder="Enter Email"
                    />
                  </div>
                </div>

                <div className="border-b mb-3 border-primary border-dashed"></div>

                {/* Address Section */}
                <div className="rounded bg-slate-50 px-3 py-3">
                  <div className="grid grid-cols-3 gap-x-2 mb-5">
                    <div className="col-span-3">
                      <p className="text-sm text-primary opacity-80 text-center">
                        * Select the tab where you want your food delivered
                      </p>
                    </div>
                  </div>

                  <div className="rounded bg-slate-50 px-4 py-4">
                    {/* Address Type Tabs */}
                    <div className="flex gap-2 mb-4">
                      {["home", "office", "apartment"].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => changeAddressPlace(type)}
                          className={`px-4 py-1.5 rounded-full border text-sm capitalize
                            ${addressPlace === type ? "bg-primary text-white border-primary" : "bg-white hover:bg-gray-100"}`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>

                    {/* Map + Landmark Search */}
                    <div className="mb-4">
                      <MapWithConditionalRendering
                        coordinates={coordinates}
                        onDragEnd={onDragEnd}
                        onReverseGeocode={handleReverseGeocode}
                        showMap={true}
                        onPlaceSelect={handlePlaceSelect}
                      />
                    </div>

                    {/* Address Preview Card */}
                    <div className="bg-white border rounded-lg p-3 mb-4 text-sm space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-700">
                          Detected Address
                        </span>
                        {nearLandmark && (
                          <span className="text-xs text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                            Near {nearLandmark}
                          </span>
                        )}
                      </div>
                      <p className="text-gray-700">
                        {addressPlace === "home" &&
                          orderDeliveryAddress.home_street_address}
                        {addressPlace === "office" &&
                          orderDeliveryAddress.office_street_address}
                        {addressPlace === "apartment" &&
                          orderDeliveryAddress.apartment_street_address}
                      </p>
                      <div className="flex gap-3 text-xs text-gray-500">
                        <span>
                          House:{" "}
                          {orderDeliveryAddress.home_house_no ||
                            orderDeliveryAddress.office_building_no ||
                            orderDeliveryAddress.apartment_apartment_no}
                        </span>
                        <span>
                          City:{" "}
                          {orderDeliveryAddress.home_city ||
                            orderDeliveryAddress.office_city ||
                            orderDeliveryAddress.apartment_city}
                        </span>
                      </div>
                    </div>

                    {/* Type-Specific Extra Fields */}
                    {addressPlace === "home" && (
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          className="border rounded-md p-2 text-sm"
                          placeholder="House / Plot No"
                          value={orderDeliveryAddress.home_house_no}
                          onChange={(e) =>
                            updateOrderDeliveryAddress({
                              home_house_no: e.target.value,
                            })
                          }
                        />
                        <input
                          className="border rounded-md p-2 text-sm"
                          placeholder="Additional direction"
                          value={orderDeliveryAddress.home_addition_direction}
                          onChange={(e) =>
                            updateOrderDeliveryAddress({
                              home_addition_direction: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                    {addressPlace === "office" && (
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          className="border rounded-md p-2 text-sm"
                          placeholder="Company"
                          value={orderDeliveryAddress.office_company}
                          onChange={(e) =>
                            updateOrderDeliveryAddress({
                              office_company: e.target.value,
                            })
                          }
                        />
                        <input
                          className="border rounded-md p-2 text-sm"
                          placeholder="Floor"
                          value={orderDeliveryAddress.office_floor}
                          onChange={(e) =>
                            updateOrderDeliveryAddress({
                              office_floor: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                    {addressPlace === "apartment" && (
                      <div className="grid grid-cols-2 gap-2">
                        <input
                          className="border rounded-md p-2 text-sm"
                          placeholder="Apartment Name"
                          value={orderDeliveryAddress.apartment_name}
                          onChange={(e) =>
                            updateOrderDeliveryAddress({
                              apartment_name: e.target.value,
                            })
                          }
                        />
                        <input
                          className="border rounded-md p-2 text-sm"
                          placeholder="Floor"
                          value={orderDeliveryAddress.apartment_floor}
                          onChange={(e) =>
                            updateOrderDeliveryAddress({
                              apartment_floor: e.target.value,
                            })
                          }
                        />
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-b border-t pt-5 pb-3 border-primary border-dashed mb-4">
                  <div className="relative">
                    <h4 className="text-sm font-semibold bg-white absolute -top-2 left-2 px-1 z-20">
                      Delivery Instruction
                    </h4>
                    <textarea
                      className="border pt-3 rounded-md w-full h-[100px]"
                      value={orderDeliveryAddress.delivery_instruction}
                      onChange={(e) =>
                        updateOrderDeliveryAddress({
                          delivery_instruction: e.target.value,
                        })
                      }
                      placeholder="Type Query..."
                    />
                  </div>
                </div>

                <div className="border-b border-primary border-dashed pb-4 mb-4">
                  <p className="text-sm text-primary opacity-80 text-center -mt-2 mb-5">
                    * To change the delivery time, remove this item and place a
                    new order.
                  </p>
                  <div className="relative grid grid-cols-1 md:grid-cols-2 gap-2">
                    <div className="relative">
                      <h4 className="text-sm font-semibold bg-white absolute -top-2 left-2 px-1 z-20">
                        Selected Delivery Date{" "}
                        <span className="text-primary">*</span>
                      </h4>
                      <input
                        type="text"
                        className="w-full border rounded p-2"
                        value={order.delivery_time}
                        disabled
                      />
                    </div>
                    <div className="relative">
                      <h4 className="text-sm font-semibold bg-white absolute -top-2 left-2 px-1 z-20">
                        Selected Delivery Time{" "}
                        <span className="text-primary">*</span>
                      </h4>
                      <input
                        type="text"
                        className="w-full border rounded p-2"
                        value={
                          convertTo12Hour(order.delivery_slot?.split("-")[0]) +
                          " - " +
                          convertTo12Hour(order.delivery_slot?.split("-")[1])
                        }
                        disabled
                      />
                    </div>
                  </div>
                </div>

                {/* Promo Code */}
                <div className="border-b border-primary border-dashed pb-4 mb-4 relative">
                  <h4 className="text-sm font-semibold bg-white absolute -top-2 left-2 px-1 z-20">
                    Promo code or Gift card
                  </h4>
                  <div className="relative">
                    <input
                      className="border rounded-md w-full pt-2"
                      value={promoCode.code}
                      onChange={(e) =>
                        setPromoCode((prev) => ({
                          ...prev,
                          code: e.target.value,
                        }))
                      }
                      placeholder="Promo Code"
                    />
                    <button
                      type="button"
                      onClick={handlePromoCodeSubmit}
                      className="text-[10px] font-semobold bg-primary px-2 py-1 text-white rounded-md absolute right-2 top-[50%] translate-y-[-50%]"
                    >
                      Submit
                    </button>
                  </div>
                </div>

                {/* Tip Section */}
                <div className="mt-6 border-b border-primary border-dashed pb-2">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-xl uppercase text-secondary tracking-widest">
                      Tip Shef:
                    </h2>
                    <h2 className="font-semibold text-2xl uppercase text-primary tracking-widest">
                      {order.tip_price.toLocaleString("en-PK", {
                        style: "currency",
                        currency: "PKR",
                      })}
                    </h2>
                  </div>
                  <div className="grid lg:grid-cols-8 md:grid-cols-8 grid-cols-4 gap-3 mb-4">
                    {[0, 10, 15, 20, 25].map((percent, idx) => (
                      <div
                        key={idx}
                        className={`chefDateBtn ${activeButton === `btn${idx + 1}` ? "active" : ""}`}
                        onClick={() =>
                          handleButtonClick(`btn${idx + 1}`, percent)
                        }
                      >
                        <h4 className="text-[14px] font-semibold mb-0 leading-tight">
                          {percent === 0 ? "No Tip" : `${percent}%`}
                        </h4>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-6">
                  <p>
                    By placing your order, you agree to Shef's updated{" "}
                    <Link to="/terms-of-services">Terms of Service</Link>,{" "}
                    <Link to="/privacy-policy">Privacy Policy</Link>, and to
                    receive order updates.
                  </p>
                </div>
              </div>

              {/* Payment Details */}
              <div className="border border-primary border-dashed rounded-lg p-4 mt-6">
                <h2 className="font-semibold text-xl uppercase text-secondary tracking-widest">
                  Payment details
                </h2>
                <div className="flex items-center gap-x-4 mt-6">
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="delivery"
                      name="paymentMethod"
                      value={order.payment_mode}
                      className="mr-2 w-4 h-4"
                      defaultChecked
                      onChange={() => updateOrder({ payment_mode: 1 })}
                    />
                    <label htmlFor="delivery" className="text-lg font-medium">
                      Cash on Delivery
                    </label>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="radio"
                      id="card"
                      name="paymentMethod"
                      value="card"
                      className="mr-2 w-4 h-4"
                      disabled
                    />
                    <label
                      htmlFor="card"
                      className="text-lg font-medium text-gray-500"
                    >
                      Card (Unavailable)
                    </label>
                  </div>
                </div>
              </div>

              {kitchenOffline && (
                <div className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  This shef's kitchen is currently offline and not accepting
                  new orders. Please try again later.
                </div>
              )}
              {!kitchenOffline && offlineStartsIn !== null && (
                <div className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3">
                  This shef's kitchen is about to go offline in{" "}
                  {offlineStartsIn === 1 ? "about a minute" : `~${offlineStartsIn} minutes`}
                  . Please hurry up and place your order soon.
                </div>
              )}
              <div className="mt-4 text-center">
                <button
                  disabled={
                    isPending ||
                    isBykeaLoading ||
                    kitchenOffline ||
                    cartItem.length < 1
                  }
                  className="bg-primary text-white text-lg w-full uppercase px-6 py-2 font-semibold rounded-lg disabled:opacity-60"
                >
                  Place Order
                </button>
              </div>
            </form>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-5 col-span-12">
            <div className="md:p-4 p-3 bg-primaryLight rounded-lg sticky top-0">
              <h3 className="mb-6 border-b border-primary border-dashed pb-2 font-semibold text-xl uppercase text-secondary tracking-widest">
                Your order for delivery
              </h3>
              <div>
                {cartItem.map(
                  (chef, chefIndex) =>
                    chef.id === parseInt(chefId) &&
                    chefIndex === parseInt(chef_index) && (
                      <div key={chefIndex}>
                        <div className="flex items-center gap-x-2 bg-primaryLight p-2 rounded-lg">
                          <img
                            src={
                              chef.profile_pic && isValidURL(chef.profile_pic)
                                ? chef.profile_pic
                                : "https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png"
                            }
                            className="object-top rounded-full w-[30px] object-cover h-[30px]"
                            alt="chef"
                          />
                          <Link
                            to={shefDetailPath(chef.id, chef.first_name, chef.last_name)}
                            className="!underline !text-secondary text-base font-semibold"
                          >
                            {`${chef.first_name || ""} ${chef.last_name || ""}`}
                          </Link>
                        </div>
                        {chef.menu.map((menu, menuIndex) => (
                          <div
                            key={menuIndex}
                            className="flex items-center justify-between border border-primary border-dashed rounded-lg p-2 gap-x-2 mt-4"
                          >
                            <div className="flex items-center gap-x-2 w-[65%]">
                              <img
                                src={
                                  menu.logo && isValidURL(menu.logo)
                                    ? menu.logo
                                    : "https://t4.ftcdn.net/jpg/04/70/29/97/360_F_470299797_UD0eoVMMSUbHCcNJCdv2t8B2g1GVqYgs.jpg"
                                }
                                className="object-top rounded-lg w-[60px] object-cover h-[60px]"
                                alt="menu"
                              />
                              <div>
                                <h3 className="mb-1 text-base font-semibold leading-tight">
                                  {menu.name}
                                </h3>
                                <div className="flex items-center gap-x-3">
                                  <div className="flex items-center justify-between w-[55%] bg-primaryLight rounded-lg">
                                    <button
                                      onClick={() =>
                                        updateQuantityInStore(
                                          chefIndex,
                                          menuIndex,
                                          menu.quantity,
                                          "decrement",
                                        )
                                      }
                                      className="w-[25%]"
                                    >
                                      -
                                    </button>
                                    <input
                                      value={menu.quantity}
                                      className="w-[50%] text-center border-0 bg-transparent text-xs px-1 h-[30px]"
                                      readOnly
                                      placeholder="1"
                                    />
                                    <button
                                      onClick={() =>
                                        updateQuantityInStore(
                                          chefIndex,
                                          menuIndex,
                                          menu.quantity,
                                          "increment",
                                        )
                                      }
                                      className="w-[25%]"
                                    >
                                      +
                                    </button>
                                  </div>
                                  <h4 className="text-lg fontsemibold mb-0">
                                    x{" "}
                                    {menu.unit_price.toLocaleString("en-PK", {
                                      style: "currency",
                                      currency: "PKR",
                                    })}
                                  </h4>
                                </div>
                              </div>
                            </div>
                            <div
                              onClick={() => {
                                dispatch(
                                  removeFromCart({ chefIndex, menuIndex }),
                                );
                                navigate("/cart", { replace: true });
                                toast.success(
                                  `${menu?.name} is removed from cart`,
                                );
                              }}
                              className="cursor-pointer"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                width="24"
                                height="24"
                                fill="rgba(0,0,0,1)"
                              >
                                <path d="M17 6H22V8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8H2V6H7V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V6ZM18 8H6V20H18V8ZM9 11H11V17H9V11ZM13 11H15V17H13V11ZM9 4V6H15V4H9Z"></path>
                              </svg>
                            </div>
                          </div>
                        ))}
                      </div>
                    ),
                )}
              </div>
            </div>

            {/* Totals */}
            <div className="md:p-4 p-3 bg-primaryLight rounded-lg mt-6">
              <h3 className="mb-6 border-b border-primary border-dashed pb-2 font-semibold text-xl uppercase text-secondary tracking-widest">
                Order Summary
              </h3>
              {[
                { label: "Subtotal", value: order.sub_total, bold: true },
                { label: "Delivery Fee", value: order.delivery_price, loading: isBykeaLoading },
                { label: "Fees & Taxes", value: order.service_fee },
                { label: "Shef Tip", value: order.tip_price },
                { label: "Discount", value: order.discount_price },
              ].map(({ label, value, bold, loading }) => (
                <div key={label} className="flex justify-between gap-2 mb-1">
                  <h3
                    className={`text-lg mb-0 ${bold ? "font-bold" : "font-medium"}`}
                  >
                    {label}
                  </h3>
                  <h4
                    className={`text-lg mb-0 ${bold ? "font-bold" : "font-medium"} ${loading ? "text-gray-400 italic" : ""}`}
                  >
                    {loading ? "Calculating..." : value.toLocaleString("en-PK", {
                      style: "currency",
                      currency: "PKR",
                    })}
                  </h4>
                </div>
              ))}
              <div className="flex justify-between gap-2 mb-1 bg-primaryLight py-2 px-3 rounded-md">
                <h3 className="text-lg font-bold mb-0">Total</h3>
                <h4 className="text-lg font-bold mb-0">
                  {order.total_price.toLocaleString("en-PK", {
                    style: "currency",
                    currency: "PKR",
                  })}
                </h4>
              </div>
              <div className="bg-gray-100 text-gray-700 text-sm rounded-md px-3 py-2 mt-2">
                <p>
                  <span className="font-semibold">Note:</span> Delivery price
                  may vary based on time and location.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Existing Address Modal */}
      <Modal
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        contentLabel="Address"
      >
        <div className="flex items-center justify-between border-b pb-3 gap-3">
          <h2 className="text-lg font-semibold leading-tight mb-0">
            Existing Address
          </h2>
          <button onClick={onRequestClose}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="rgba(0,0,0,1)"
            >
              <path d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"></path>
            </svg>
          </button>
        </div>
        {userInfo.user_addresses?.length > 0 && (
          <div>
            <h3 className="mb-5 text-lg text-gray my-2 font-semibold">
              Select Address
            </h3>
            <ul className="flex flex-wrap gap-2">
              {userInfo.user_addresses.map((address, index) => (
                <li className="min-w-[200px]" key={index}>
                  <input
                    onChange={() => onSelectExistingAddress(address)}
                    type="radio"
                    id={index}
                    name="hosting"
                    className="hidden peer"
                    required
                  />
                  <label
                    htmlFor={index}
                    className="inline-flex items-center justify-between w-full p-5 text-gray-500 bg-white border border-gray-200 rounded-lg cursor-pointer peer-checked:border-blue-600 peer-checked:text-blue-600 hover:text-gray-600 hover:bg-gray-100"
                  >
                    <div className="block">
                      <div className="w-full">
                        {`${address?.address ? address.address + ", " : ""}${address?.city || ""}`}
                      </div>
                    </div>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal>

      {/* Error Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => { }}
        contentLabel="Error"
        shouldCloseOnOverlayClick={false}
        className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[400px] bg-white rounded-lg shadow-xl p-6"
        overlayClassName="fixed inset-0 bg-gray-900 bg-opacity-50"
      >
        <div className="flex items-center justify-start border-b pb-3 mb-4">
          <h2 className="text-lg font-semibold text-gray-800 leading-tight">
            Location Access Required
          </h2>
        </div>
        <div className="flex flex-col items-center space-y-4">
          <p className="text-gray-600 text-center">{error}</p>
        </div>
      </Modal>
    </>
  );
};

export default Checkout;
